/**
 * Locked-tree refinements for screenshot-node wait (ERROR_STATES S1–S4 + sibling coherence).
 * Validates waitEnabled screenshot nodes during macro tree lock: expected state,
 * timing fields, and kind restrictions. Draft schema must not call this.
 *
 * Sibling bans (old S5) are replaced by wait-sibling coherence in macroTree.ts.
 */
import { z } from 'zod';

export interface ScreenshotWaitLockNode {
  id: string;
  kind: string;
  waitEnabled?: boolean;
  expectedState?: { snapshots: readonly unknown[] };
  waitIntervalMs?: number;
  waitTimeoutMs?: number;
}

export interface ScreenshotWaitLockGraph {
  nodes: readonly ScreenshotWaitLockNode[];
  edges: readonly { source: string; target: string }[];
}

/**
 * Adds lock issues for `waitEnabled === true` nodes. Returns after the first
 * issue so messages stay unique in tests (same pattern as wait-node refine).
 */
export function refineScreenshotWaitLock(graph: ScreenshotWaitLockGraph, ctx: z.RefinementCtx): void {
  for (const node of graph.nodes) {
    if (node.waitEnabled !== true) continue;

    if (node.kind === 'wait') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `node ${node.id} has waitEnabled but kind is 'wait' — Wait nodes cannot use screenshot-node wait`,
        path: ['nodes'],
      });
      return;
    }

    if (!node.expectedState || node.expectedState.snapshots.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `node ${node.id} has waitEnabled but no expectedState — screenshot-node wait requires a screenshot`,
        path: ['nodes'],
      });
      return;
    }

    if (node.waitIntervalMs === undefined || node.waitTimeoutMs === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `node ${node.id} has waitEnabled but waitIntervalMs/waitTimeoutMs are required on a locked tree`,
        path: ['nodes'],
      });
      return;
    }

    if (node.waitTimeoutMs < node.waitIntervalMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `node ${node.id} waitTimeoutMs (${node.waitTimeoutMs}) must be >= waitIntervalMs (${node.waitIntervalMs}) when waitEnabled`,
        path: ['nodes'],
      });
      return;
    }
  }
}
